import React, { forwardRef } from "react";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SentimentDissatisfiedRoundedIcon from "@mui/icons-material/SentimentDissatisfiedRounded";
import { Button, Dialog, DialogContent, Grid, Slide, Typography } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import useEvent from "@/hooks/useEvent";
import usePlatform from "@/hooks/usePlatform";

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface MessageDialogProps {
  open: boolean;
  onOpen: (isOpen: boolean) => void;
  status?: 0 | 1;
  message?: string;
}

const MessageDialog = ({ message = "", status = 1, open, onOpen }: MessageDialogProps) => {
  const { isMobile } = usePlatform();

  const handleClose = useEvent(() => {
    onOpen(false);
  });

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      sx={{
        maxWidth: "430px",
        margin: "auto",
        "& .MuiDialog-paper": {
          margin: 0,
          borderRadius: "12px",
          width: "100%",
          maxWidth: "calc(100% - 32px)",
        },
        "& .MuiDialogContent-root": {
          padding: isMobile ? "20px" : "20px 24px",
        },
      }}
      onClose={handleClose}
    >
      <DialogContent>
        <div className="flex flex-col w-full items-center justify-center py-[10px]">
          {status ? (
            <CheckCircleOutlineIcon
              className="!text-[50px] mb-[10px]"
              sx={{ color: (theme) => theme.palette.success.light }}
            />
          ) : (
            <SentimentDissatisfiedRoundedIcon
              className="!text-[50px] mb-[10px]"
              sx={{ color: (theme) => theme.palette.error.dark }}
            />
          )}

          <Typography variant="body1" className="!text-[18px] pb-[10px]">
            {message}
          </Typography>

          <Grid
            container
            className="!mt-[16px]"
            columnSpacing={2}
            rowGap={2}
            direction={isMobile ? "column-reverse" : "row"}
          >
            <Grid item xs={isMobile ? 12 : 12}>
              <Button variant="outlined" fullWidth onClick={handleClose}>
                OK
              </Button>
            </Grid>
          </Grid>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(MessageDialog);
